import { apiClient, refreshAccessTokenSingleflight } from '../lib/api';
import type { AuthUser, AuthMembership, MembershipOption } from '../types/identity';
import type {
  LoginCredentials as LegacyLoginCredentials,
  SignupData,
  AuthResponse,
  GoogleAuthResponse,
  RefreshTokenResponse,
  ChangePasswordData,
  GoogleAuthData,
  GoogleSignupData,
  User,
} from '../types/auth';
import type { SuccessResponse } from '../types/api';

// ── Unified identity types (Task 3) ─────────────────────────────────────────

export interface LoginResult {
  requires_tenant_selection: boolean;
  user: AuthUser;
  tenant?: AuthMembership['tenant'];
  role?: string;
  perms?: string[];
  access_token?: string;
  memberships?: MembershipOption[];
  /** True when the active membership still has to acknowledge the current
   *  Terms & Privacy versions. Only /auth/me populates it today — treat an
   *  absent field as `false`. */
  legal_ack_required?: boolean;
}

export interface LoginCredentials {
  email: string;
  password: string;
  tenant_domain?: string;
}

// apiClient already returns the response body `{ status, data, message, timestamp }`,
// so the payload is one `.data` deep.
const unwrap = <T>(body: { data: T }): T => body.data;

export async function login(creds: LoginCredentials): Promise<LoginResult> {
  return unwrap<LoginResult>(await apiClient.post('/auth/login', creds));
}

export async function selectTenant(tenant_id: string): Promise<LoginResult> {
  return unwrap<LoginResult>(await apiClient.post('/auth/select-tenant', { tenant_id }));
}

export async function switchTenant(tenant_id: string): Promise<LoginResult> {
  return unwrap<LoginResult>(await apiClient.post('/auth/switch-tenant', { tenant_id }));
}

export async function logout(): Promise<void> {
  await apiClient.post('/auth/logout');
}

export async function getMe(): Promise<LoginResult> {
  return unwrap<LoginResult>(await apiClient.get('/auth/me'));
}

// ── Legacy authService object (preserved for backward compatibility) ──────────

export interface UpdateMeData {
  first_name?: string;
  last_name?: string;
  avatar_url?: string | null;
  role_id?: string | null;
  department_id?: string | null;
  location_id?: string | null;
}

export interface UpdateMeResponse {
  status: 'success';
  data: { user: User };
}

export const authService = {
  /** POST /api/v1/auth/login */
  login: async (credentials: LegacyLoginCredentials): Promise<AuthResponse> => {
    return apiClient.post<AuthResponse>('/auth/login', credentials);
  },

  /** POST /api/v1/auth/signup */
  signup: async (data: SignupData): Promise<AuthResponse> => {
    return apiClient.post<AuthResponse>('/auth/signup', data);
  },

  /** POST /api/v1/auth/google/signup — Google identity + org/legal questionnaire */
  googleSignup: async (data: GoogleSignupData): Promise<AuthResponse> => {
    return apiClient.post<AuthResponse>('/auth/google/signup', data);
  },

  /** POST /api/v1/auth/refresh */
  refreshToken: async (): Promise<RefreshTokenResponse> => {
    return (await refreshAccessTokenSingleflight()) as RefreshTokenResponse;
  },

  /** POST /api/v1/auth/logout */
  logout: async (): Promise<SuccessResponse> => {
    return apiClient.post<SuccessResponse>('/auth/logout');
  },

  /** GET /api/v1/auth/me */
  me: async (): Promise<AuthResponse> => {
    return apiClient.get<AuthResponse>('/auth/me');
  },

  /** PUT /api/v1/auth/me */
  updateMe: async (updateData: UpdateMeData): Promise<UpdateMeResponse> => {
    return apiClient.put<UpdateMeResponse>('/auth/me', updateData);
  },

  /** POST /api/v1/auth/forgot-password */
  forgotPassword: async (email: string): Promise<SuccessResponse> => {
    return apiClient.post<SuccessResponse>('/auth/forgot-password', { email });
  },

  /** POST /api/v1/auth/reset-password */
  resetPassword: async (
    email: string,
    code: string,
    newPassword: string
  ): Promise<SuccessResponse> => {
    return apiClient.post<SuccessResponse>('/auth/reset-password', {
      email,
      code,
      new_password: newPassword,
    });
  },

  /** POST /api/v1/auth/change-password */
  changePassword: async (changeData: ChangePasswordData): Promise<SuccessResponse> => {
    return apiClient.post<SuccessResponse>('/auth/change-password', changeData);
  },

  /** POST /api/v1/auth/google */
  googleLogin: async (googleData: GoogleAuthData): Promise<GoogleAuthResponse> => {
    return apiClient.post<GoogleAuthResponse>('/auth/google', googleData);
  },
};
